import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  ContactPhone as ContactIcon,
} from "@mui/icons-material";
import { apiCall } from '../../../services/ApiCall';
import config from '../../../config/config.json';

const MainContact = ({ onPrevious, siteData, setShowDetails }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  // Original state variables (unchanged)
  const [formData, setFormData] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, contactId: null, contactName: '' });

  const primaryColor = "#0d6efd";

  // Original API function (unchanged logic)
  const getSitesContact = async () => {
    // Add null check for siteData
    if (!siteData || !siteData.id) {
      console.error("No site data provided");
      return;
    }

    try {
      const { isSuccess, data } = await apiCall(
        `${config.POST_LOGIN_API_URL}getSitesContact`,
        {},
        { siteId: siteData.id },
        "GET"
      );
      if (isSuccess) {
        setFormData(
          (data?.data?.sitesUserData || []).map(contact => ({ ...contact }))
        );
      }
    } catch (error) {
      console.error("Error fetching site contacts:", error);
      setSnackbar({ open: true, message: "Failed to load contacts", severity: "error" });
    }
  };

  // Original useEffect (unchanged logic)
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
    }
    getSitesContact();
  }, [siteData.id]);

  // Original event handler (unchanged logic)
  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
   setFormData(prev =>
  prev.map((contact, i) =>
    i === index ? { ...contact, [name]: value } : contact
  )
);

  };

  // Original save function (unchanged logic)
  const handleSave = async () => {
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POST_LOGIN_API_URL}updateSitesData`,
        {},
        { siteId: siteData.id, ...siteData, userData: formData },
        "POST"
      );
      if (isSuccess) {
        setShowDetails(false);
        setSnackbar({ open: true, message: "Site contacts updated successfully!", severity: "success" });
      }
    } catch (error) {
      console.error("Error saving contacts:", error);
      setSnackbar({ open: true, message: "Failed to save contacts", severity: "error" });
    }
  };

  // Original delete function (unchanged logic)
  const handleDeleteContact = async (userId) => {
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POST_LOGIN_API_URL}deleteSitesAndContact`,
        {},
        { siteId: siteData.id, userId },
        "POST"
      );
      if (isSuccess) {
        getSitesContact();
        setDeleteDialog({ open: false, contactId: null, contactName: '' });
        setSnackbar({ open: true, message: "Contact deleted successfully!", severity: "success" });
      }
    } catch (error) {
      console.error("Error deleting contact:", error);
      setSnackbar({ open: true, message: "Failed to delete contact", severity: "error" });
    }
  };

  const handleDeleteClick = (contact) => {
    setDeleteDialog({
      open: true,
      contactId: contact.id,
      contactName: `${contact.fname} ${contact.lname}`.trim() || 'this contact'
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteDialog.contactId) {
      handleDeleteContact(deleteDialog.contactId);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '100%' }}>
      {/* Site Contact Section */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{
          bgcolor: primaryColor,
          color: 'white',
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <ContactIcon sx={{ fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Edit Site Contact
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{
          bgcolor: '#f8f9fa',
          p: { xs: 2, md: 3 }
        }}>
          {formData && formData.length > 0 ? (
            <Stack spacing={3}>
              {formData.map((contact, index) => (
                <Card
                  key={contact.id || index}
                  elevation={0}
                  sx={{
                    border: `2px solid ${primaryColor}30`,
                    borderRadius: 2,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      boxShadow: theme.shadows[3],
                      borderColor: `${primaryColor}60`,
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <PersonIcon sx={{ color: primaryColor, fontSize: 24 }} />
                        <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 600 }}>
                          Contact {index + 1}
                        </Typography>
                      </Box>
                      <IconButton
                        onClick={() => handleDeleteClick(contact)}
                        sx={{
                          color: theme.palette.error.main,
                          border: `1px solid ${theme.palette.error.main}`,
                          borderRadius: 1,
                          '&:hover': {
                            backgroundColor: `${theme.palette.error.main}10`,
                          }
                        }}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* First Row - Names */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="First Name"
                          name="fname"
                          value={contact.fname || ''}
                          onChange={(e) => handleInputChange(e, index)}
                          size={isSmall ? "small" : "medium"}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: primaryColor + '40',
                              },
                              '&:hover fieldset': {
                                borderColor: primaryColor,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: primaryColor,
                              },
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: primaryColor,
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Last Name"
                          name="lname"
                          value={contact.lname || ''}
                          onChange={(e) => handleInputChange(e, index)}
                          size={isSmall ? "small" : "medium"}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: primaryColor + '40',
                              },
                              '&:hover fieldset': {
                                borderColor: primaryColor,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: primaryColor,
                              },
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: primaryColor,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>

                    {/* Second Row - Contact Info */}
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          label="Email"
                          name="email"
                          type="email"
                          value={contact.email || ''}
                          onChange={(e) => handleInputChange(e, index)}
                          size={isSmall ? "small" : "medium"}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: primaryColor + '40',
                              },
                              '&:hover fieldset': {
                                borderColor: primaryColor,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: primaryColor,
                              },
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: primaryColor,
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          label="Phone"
                          name="phone"
                          type="tel"
                          value={contact.phone || ''}
                          onChange={(e) => handleInputChange(e, index)}
                          size={isSmall ? "small" : "medium"}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: primaryColor + '40',
                              },
                              '&:hover fieldset': {
                                borderColor: primaryColor,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: primaryColor,
                              },
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: primaryColor,
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={12} md={4}>
                        <FormControl
                          fullWidth
                          size={isSmall ? "small" : "medium"}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: primaryColor + '40',
                              },
                              '&:hover fieldset': {
                                borderColor: primaryColor,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: primaryColor,
                              },
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: primaryColor,
                            },
                          }}
                        >
                          <InputLabel>Designation</InputLabel>
                          <Select
                            name="designation"
                            value={contact.designation || ''}
                            onChange={(e) => handleInputChange(e, index)}
                            label="Designation"
                          >
                            <MenuItem value="">Select Designation</MenuItem>
                            <MenuItem value="Site Admin">Site Admin</MenuItem>
                            <MenuItem value="Site Manager">Site Manager</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
              textAlign: 'center'
            }}>
              <ContactIcon sx={{
                fontSize: 64,
                color: theme.palette.grey[300],
                mb: 2
              }} />
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.grey[500],
                  fontWeight: 500,
                  mb: 1
                }}
              >
                No Contacts Found
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
              >
                There are no contacts associated with this site.
              </Typography>
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            mt: 4,
            flexWrap: 'wrap'
          }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={onPrevious}
              sx={{
                borderColor: primaryColor,
                color: primaryColor,
                '&:hover': {
                  backgroundColor: `${primaryColor}10`,
                  borderColor: primaryColor,
                },
                minWidth: 120,
              }}
            >
              Previous
            </Button>

            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              sx={{
                backgroundColor: primaryColor,
                '&:hover': {
                  backgroundColor: primaryColor,
                  opacity: 0.9,
                },
                minWidth: 120,
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, contactId: null, contactName: '' })}>
        <DialogTitle>Delete Contact</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {deleteDialog.contactName}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, contactId: null, contactName: '' })}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MainContact;